<template>
    <div class="h-screen bg-background flex flex-col">
        <!-- Connection Reset Handler (invisible component) -->
        <ConnectionResetHandler />
        <main class="flex-1 flex flex-col overflow-hidden">
            <!-- Authentication Forms with Airport Background -->
            <div
                    v-if="!isAuthenticated"
                    class="relative h-full bg-cover bg-center"
                    :style="{ backgroundImage: `url(${require('@/assets/airport-background.jpg')})` }"
            >
                <!-- Blur overlay -->
                <div class="absolute inset-0 backdrop-blur-lg bg-black/40"></div>

                <!-- Content -->
                <div class="relative z-10 h-full flex items-center justify-center">
                    <div class="max-w-7xl w-full px-4 sm:px-6 lg:px-8">
                        <div class="flex items-center justify-center">
                            <div class="flex items-center max-w-6xl w-full">
                                <!-- Welcome Text Section (Left Side) -->
                                <div class="w-1/2 pr-8">
                                    <div class="mb-6">
                                        <img src="./assets/Fraport_Logo_Slovenija.svg" alt="Fraport Slovenija Logo"
                                             class="h-24 w-auto invert brightness-0 filter">
                                    </div>
                                    <h1 class="text-4xl font-bold text-white mb-4 text-shadow">Welcome to Fraport
                                        Insights - Your Intelligent Airport Management Solution</h1>
                                    <p class="text-xl text-white text-shadow">Streamline airport operations with
                                        advanced forecasting and AI-powered workflow optimization.</p>
                                </div>

                                <!-- Form Section (Right Side) -->
                                <div class="w-1/2 flex justify-center">
                                    <div class="w-full max-w-md">
                                        <LoginForm
                                                v-if="showLoginForm"
                                                @login-success="handleLoginSuccess"
                                                @switch-to-register="showRegisterForm = true; showLoginForm = false"
                                        />
                                        <RegisterForm
                                                v-else
                                                @register-success="handleRegisterSuccess"
                                                @switch-to-login="showRegisterForm = false; showLoginForm = true"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main Content with Side Navigation (shown after login) -->
            <div v-else class="flex h-full">
                <!-- Side Navigation -->
                <SideNav
                        :activeItem="activeNavItem"
                        :userData="userData"
                        @navigate="handleNavigation"
                        @logout="logout"
                        @toggle-collapse="handleSideNavCollapse"
                />

                <!-- Content Area Logged in -->
                <div class="flex-1 overflow-auto p-6 transition-all duration-300 bg-gray-50">
                    <!-- Session refreshing indicator -->
                    <div v-if="isSessionRefreshing"
                         class="fixed top-4 right-4 bg-blue-100 text-blue-800 px-4 py-2 rounded-md shadow-md z-50">
            <span class="inline-block mr-2">
              <svg class="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
                        Refreshing session...
                    </div>

                    <!-- Session validation error -->
                    <div v-if="isAuthenticated && !isSessionValid && !isSessionRefreshing"
                         class="flex flex-col items-center justify-center h-full">
                        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 max-w-md">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg"
                                         viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd"
                                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                              clip-rule="evenodd"/>
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <p class="text-sm text-yellow-700">
                                        Your session appears to be invalid. Reload the page or click retry to refresh...
                                    </p>
                                    <button
                                            @click="validateSession"
                                            class="mt-2 px-4 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-md text-sm font-medium transition-colors"
                                    >
                                        Retry
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Content components - only shown when session is valid -->
                    <template v-if="isSessionValid">
                        <Dashboard v-if="activeNavItem === 'dashboard'"/>
                        <EmailGenerator v-else-if="activeNavItem === 'email-generator'"/>
                        <UserSettings
                                v-else-if="activeNavItem === 'user-settings'"
                                :userData="{
                ...userData,
                role: userData.system_role === 'ADMIN' ? 'Administrator' : 'User',
                business_role: userData.business_role as BusinessRole
              }"
                                @update-user-data="updateUserData"
                        />
                        <SupabaseTest v-else-if="activeNavItem === 'supabase-test'"/>
                        <StorageTest v-else-if="activeNavItem === 'storage-test'"/>
                        <ForecastingPCO v-else-if="activeNavItem === 'forecasting-pco'"
                                        :userData="userData"/>
                        <Chatbot
                                v-else-if="activeNavItem === 'chatbot'"
                                :userData="userData"
                        />
                        <Administration
                                v-else-if="activeNavItem === 'administration'"
                                :initialView="administrationSubView"
                        />
                        <ForecastingPlanners
                                v-else-if="userData && activeNavItem === 'forecasting-planners'"
                                :userData="userData"/>
                        <TemplateStorageExample v-else-if="activeNavItem === 'template-storage'"/>
                        <KnowledgebaseUpload v-else-if="activeNavItem === 'knowledgebase-upload'"/>
                    </template>
                </div>
            </div>
        </main>
    </div>
</template>

<script lang="ts">
import {defineComponent, ref, computed, onMounted, watch} from 'vue';
import {useRouter} from 'vue-router';
import {logger, LogCategory} from './services/logger';
import LoginForm from './components/LoginForm.vue';
import RegisterForm from './components/RegisterForm.vue';
import Dashboard from './components/Dashboard.vue';
import SideNav from './components/SideNav.vue';
import {EmailGenerator} from './features/email-generation/components'
import SupabaseTest from './components/SupabaseTest.vue';
import StorageTest from './components/StorageTest.vue';
import UserSettings from './components/UserSettings.vue';
import ForecastingPCO from './components/ForecastingPCO.vue';
import ForecastingPlanners from './components/ForecastingPlanners.vue';
import Administration from './components/Administration.vue';
import Chatbot from './components/Chatbot.vue';
import TemplateStorageExample from './features/template-storage/examples/TemplateStorageExample.vue';
import KnowledgebaseUpload from './components/KnowledgebaseUpload.vue';
import ConnectionResetHandler from './components/ConnectionResetHandler.vue';
import {supabase} from './services/supabase';
import {useAuth} from './composables/useAuth';
import {useNavigation} from './composables/useNavigation';
import {SystemRole} from './types/systemRole';
import {BusinessRole} from './types/businessRole';

export default defineComponent({
    name: 'App',
    components: {
        LoginForm,
        RegisterForm,
        Dashboard,
        SideNav,
        EmailGenerator,
        SupabaseTest,
        StorageTest,
        UserSettings,
        ForecastingPCO,
        ForecastingPlanners,
        Administration,
        Chatbot,
        TemplateStorageExample,
        KnowledgebaseUpload,
        ConnectionResetHandler
    },
    setup() {
        const router = useRouter();
        const {user, profile, isAuthenticated, logout: authLogout, init, checkAndRefreshAuth} = useAuth();
        const {activeNavItem, administrationSubView} = useNavigation();

        const showLoginForm = ref(true);
        const showRegisterForm = ref(false);
        const sideNavCollapsed = ref(false);
        const isSessionValid = ref(false);
        const isSessionRefreshing = ref(false);
        
        // Track if a session validation is already in progress
        const isValidatingSession = ref(false);
        // Session validation timeout reference
        let sessionValidationTimeout: number | null = null;

        console.log("Process Envs ", process.env)

        // Computed property to format user data for components
        const userData = computed(() => {
            if (!user.value || !profile.value) {
                return {
                    name: '',
                    email: '',
                    username: '',
                    system_role: SystemRole.USER,
                    avatar: null,
                    business_role: BusinessRole.PLANNER // Default to PLANNER if no profile
                };
            }
            // Ensure business_role is a valid enum value
            let businessRole = profile.value.business_role as string;

            // Check if the business_role is one of the valid enum values
            const validRoles = Object.values(BusinessRole);
            const isValidRole = businessRole && validRoles.includes(businessRole as any);

            if (!isValidRole) {
                businessRole = BusinessRole.PLANNER;
            }

            // Determine the system role
            let systemRole = SystemRole.USER;

            // First check if system_role is already set in the profile
            if (profile.value.system_role) {

                // Check if it's a string that matches an enum value
                if (typeof profile.value.system_role === 'string') {
                    const sysRoleLower = profile.value.system_role.toLowerCase();

                    if (sysRoleLower === 'administrator' || sysRoleLower === 'admin') {
                        systemRole = SystemRole.ADMIN;
                    } else if (sysRoleLower === 'user') {
                        systemRole = SystemRole.USER;
                    }
                }
            }
            // If no system_role, fall back to role
            else if (profile.value.role) {

                if (profile.value.role === 'Administrator') {
                    systemRole = SystemRole.ADMIN;
                } else if (profile.value.role === 'User') {
                    systemRole = SystemRole.USER;
                } else {
                    // Try case-insensitive comparison
                    const roleLower = profile.value.role.toLowerCase();

                    if (roleLower === 'administrator') {
                        systemRole = SystemRole.ADMIN;
                    } else {
                        systemRole = SystemRole.USER;
                    }
                }
            }


            return {
                id: user.value.id,
                name: profile.value.name || '',
                email: user.value.email || '',
                username: profile.value.username || '',
                system_role: systemRole,
                avatar: profile.value.avatar_url,
                business_role: businessRole,
                report_type: profile.value.report_type || undefined
            };
        });

        /**
         * Validates the current session and refreshes it if needed
         * Sets isSessionValid based on the result
         */
        async function validateSession() {
            // If validation is already in progress, don't start another one
            if (isValidatingSession.value) {
                logger.info('Session validation already in progress, skipping');
                return isSessionValid.value;
            }

            try {
                isValidatingSession.value = true;
                logger.info('Validating session');
                isSessionRefreshing.value = true;

                // Set a timeout to clear the refreshing state if it takes too long
                if (sessionValidationTimeout) {
                    clearTimeout(sessionValidationTimeout);
                }
                
                sessionValidationTimeout = window.setTimeout(() => {
                    if (isSessionRefreshing.value) {
                        logger.warn('Session validation taking too long, resetting state and forcing reconnection');
                        isSessionRefreshing.value = false;
                        isValidatingSession.value = false;
                        
                        // Force a reconnection by dispatching the force-auth-refresh event
                        if (typeof window !== 'undefined') {
                            console.log('[APP] Dispatching force-auth-refresh event due to timeout');
                            const authEvent = new CustomEvent('force-auth-refresh', {
                                detail: {
                                    timestamp: new Date().toISOString(),
                                    source: 'session-validation-timeout'
                                }
                            });
                            // eslint-disable-next-line no-undef
                            window.dispatchEvent(authEvent as unknown as Event);
                            
                            // Dispatch a force-refresh event to trigger UI updates
                            console.log('[APP] Dispatching force-refresh event due to timeout');
                            const refreshEvent = new CustomEvent('force-refresh', {
                                detail: {
                                    timestamp: new Date().toISOString(),
                                    source: 'session-validation-timeout'
                                }
                            });
                            // eslint-disable-next-line no-undef
                            window.dispatchEvent(refreshEvent as unknown as Event);
                        }
                    }
                }, 3000); // Reduced to 3 second timeout

                try {
                    // Set a shorter timeout for the auth check
                    const timeoutPromise = new Promise<boolean>((_, reject) => {
                        setTimeout(() => reject(new Error('Auth check timed out')), 2000);
                    });
                    
                    // Race the auth check against the timeout
                    const isValid = await Promise.race([
                        checkAndRefreshAuth(),
                        timeoutPromise
                    ]);
                    
                    isSessionValid.value = isValid;
    
                    if (!isValid && isAuthenticated.value) {
                        logger.warn('Session is invalid but user is marked as authenticated, forcing logout');
                        // If session is invalid but user is still marked as authenticated, force logout
                        await logout();
                    }
    
                    return isValid;
                } catch (timeoutErr) {
                    logger.error('Auth check timed out, forcing reconnection', { error: timeoutErr });
                    
                    // Force a reconnection by dispatching the force-auth-refresh event
                    if (typeof window !== 'undefined') {
                        console.log('[APP] Dispatching force-auth-refresh event due to auth check timeout');
                        const authEvent = new CustomEvent('force-auth-refresh', {
                            detail: {
                                timestamp: new Date().toISOString(),
                                source: 'auth-check-timeout'
                            }
                        });
                        // eslint-disable-next-line no-undef
                        window.dispatchEvent(authEvent as unknown as Event);
                    }
                    
                    // Assume the session is valid to prevent unnecessary logouts
                    isSessionValid.value = true;
                    return true;
                }
            } catch (err) {
                logger.error('Error validating session', {error: err});
                isSessionValid.value = false;
                return false;
            } finally {
                isSessionRefreshing.value = false;
                isValidatingSession.value = false;
                
                // Clear the timeout
                if (sessionValidationTimeout) {
                    clearTimeout(sessionValidationTimeout);
                    sessionValidationTimeout = null;
                }
            }
        }

        // Initialize auth state when component is mounted
        onMounted(async () => {
            try {
                // Force a refresh of the auth state
                const {data} = await supabase.auth.getSession();

                if (data.session) {
                    // If we have a session but it's about to expire, refresh it
                    const expiresAt = data.session.expires_at;
                    const expiresIn = expiresAt ? new Date(expiresAt * 1000).getTime() - Date.now() : 0;

                    if (expiresIn < 60 * 60 * 1000) { // Less than 1 hour remaining
                        await supabase.auth.refreshSession();
                    }
                }

                // Initialize auth state
                await init();

                // Validate the session after initialization
                await validateSession();
                
                // Set up event listeners for auth refresh events
                const forceAuthRefreshHandler = (event: CustomEvent) => {
                    logger.info('Force auth refresh event received', LogCategory.AUTH, { detail: event.detail });
                    console.log('[APP] Force auth refresh event received:', event.detail);
                    
                    // Validate the session immediately
                    validateSession();
                };
                
                // Add event listener for force-auth-refresh
                // eslint-disable-next-line no-undef
                window.addEventListener('force-auth-refresh', forceAuthRefreshHandler as unknown as EventListener);
                
                // Set up a periodic check to refresh the token if needed
                const checkInterval = setInterval(async () => {
                    if (isAuthenticated.value) {
                        logger.info('Performing periodic session check');
                        const {data} = await supabase.auth.getSession();
                        if (data.session) {
                            const expiresAt = data.session.expires_at;
                            const expiresIn = expiresAt ? new Date(expiresAt * 1000).getTime() - Date.now() : 0;

                            if (expiresIn < 60 * 60 * 1000) { // Less than 1 hour remaining
                                isSessionRefreshing.value = true;
                                await supabase.auth.refreshSession();
                                isSessionRefreshing.value = false;
                            }

                            // Validate session after refresh
                            await validateSession();
                        } else {
                            // No session found during periodic check
                            isSessionValid.value = false;
                        }
                    }
                }, 2 * 60 * 1000); // Check every 2 minutes

                // Clean up interval and event listeners on component unmount
                return () => {
                    clearInterval(checkInterval);
                    // eslint-disable-next-line no-undef
                    window.removeEventListener('force-auth-refresh', forceAuthRefreshHandler as unknown as EventListener);
                };
            } catch (err) {
                logger.error('Error initializing auth state', {error: err});
                isSessionValid.value = false;
            }
        });

        // Watch for route changes to validate session, but avoid too frequent checks
        watch(() => router.currentRoute.value.path, async (newPath, oldPath) => {
            if (newPath !== oldPath && isAuthenticated.value) {
                logger.info('Route changed', {from: oldPath, to: newPath});
                
                const now = Date.now();
                // Only validate session if it hasn't been validated recently
                if (now - lastSessionValidation.value > SESSION_VALIDATION_INTERVAL) {
                    logger.info('Validating session after route change');
                    await validateSession();
                    lastSessionValidation.value = now;
                } else {
                    logger.info('Skipping session validation (recently validated)');
                }
            }
        });

        function handleLoginSuccess() {
            activeNavItem.value = 'chatbot';
        }

        async function handleRegisterSuccess() {
            // Wait a moment for the database to fully process the registration
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Set the active navigation item for when the page reloads
            activeNavItem.value = 'chatbot';
        }

        async function logout() {
            try {
                // Call the enhanced logout function from useAuth
                await authLogout();

                // Reset UI state
                activeNavItem.value = 'dashboard';
                showLoginForm.value = true;
                showRegisterForm.value = false;

            } catch (err) {
                // If logout fails, try a more aggressive approach
                logger.error('error logging out' + err)
                window.location.href = '/';
            }
        }

        async function handleNavigation(navItem: string) {
            activeNavItem.value = navItem;

            const now = Date.now();
            // Only validate session if it hasn't been validated recently
            if (now - lastSessionValidation.value > SESSION_VALIDATION_INTERVAL) {
                await validateSession();
                lastSessionValidation.value = now;
            }

            // Navigate to the corresponding route
            if (navItem === 'user-settings') {
                router.push('/user-settings');
            } else if (navItem) {
                router.push(`/${navItem}`);
            }
        }

        function handleSideNavCollapse(isCollapsed: boolean) {
            sideNavCollapsed.value = isCollapsed;
        }

        // Function to update user data (needed for UserSettings component)
        function updateUserData(updatedData: any) {
            logger.info('User data updated', LogCategory.AUTH, {updatedData});
            // The actual profile update is handled by the UserSettings component
            // This function is just to handle the event
        }

        // Track last time session was validated to prevent too frequent checks
        const lastSessionValidation = ref(0);
        const SESSION_VALIDATION_INTERVAL = 30000; // 30 seconds
        
        // No connection status tracking or test functions needed

        return {
            isAuthenticated,
            isSessionValid,
            isSessionRefreshing,
            isValidatingSession,
            lastSessionValidation,
            showLoginForm,
            showRegisterForm,
            userData,
            activeNavItem,
            sideNavCollapsed,
            administrationSubView,
            handleLoginSuccess,
            handleRegisterSuccess,
            logout,
            handleNavigation,
            handleSideNavCollapse,
            updateUserData,
            validateSession
        };
    }
});
</script>

<style>
body {
    margin: 0;
    padding: 0;
}
</style>
